import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { ChevronDown, Download, FileText, Loader2, RefreshCw, MessageSquare, Info } from 'lucide-react';
import SlipInvoiceDetailModal from './SlipInvoiceDetailModal';

const fmtInr = (n) => `₹${Math.round(n || 0).toLocaleString('en-IN')}`;
const dayLabel = (iso) => {
  try { return new Date(iso + 'T00:00:00').toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }); }
  catch { return iso; }
};

const StateBadge = ({ state }) => state === 'done'
  ? <Badge className="bg-emerald-100 text-emerald-700 text-[9px] py-0">✓ Done</Badge>
  : state === 'partial'
    ? <Badge className="bg-amber-100 text-amber-700 text-[9px] py-0">Partial</Badge>
    : <Badge className="bg-rose-100 text-rose-700 text-[9px] py-0">Open</Badge>;

// Slip bucketing: diff (in days) = current expected − original expected. Positive = slipped later.
const SLIP_BUCKETS = [
  { key: 'ontime', label: 'On-time / Early', short: 'On-time', match: (d) => d !== null && d <= 0, cls: 'bg-emerald-50 text-emerald-700 border-emerald-300', activeCls: 'bg-emerald-600 text-white border-emerald-600' },
  { key: 's1_3', label: '1-3 d', short: '1-3d', match: (d) => d !== null && d >= 1 && d <= 3, cls: 'bg-amber-50 text-amber-700 border-amber-300', activeCls: 'bg-amber-600 text-white border-amber-600' },
  { key: 's4_7', label: '4-7 d', short: '4-7d', match: (d) => d !== null && d >= 4 && d <= 7, cls: 'bg-orange-50 text-orange-700 border-orange-300', activeCls: 'bg-orange-600 text-white border-orange-600' },
  { key: 's8_10', label: '8-10 d', short: '8-10d', match: (d) => d !== null && d >= 8 && d <= 10, cls: 'bg-orange-100 text-orange-800 border-orange-400', activeCls: 'bg-orange-700 text-white border-orange-700' },
  { key: 's11_15', label: '11-15 d', short: '11-15d', match: (d) => d !== null && d >= 11 && d <= 15, cls: 'bg-rose-50 text-rose-700 border-rose-300', activeCls: 'bg-rose-600 text-white border-rose-600' },
  { key: 's16_30', label: '16-30 d', short: '16-30d', match: (d) => d !== null && d >= 16 && d <= 30, cls: 'bg-rose-100 text-rose-800 border-rose-400', activeCls: 'bg-rose-700 text-white border-rose-700' },
  { key: 's30plus', label: '30+ d', short: '30d+', match: (d) => d !== null && d > 30, cls: 'bg-rose-200 text-rose-900 border-rose-500', activeCls: 'bg-rose-800 text-white border-rose-800' },
  { key: 'none', label: 'No baseline', short: 'No base', match: (d) => d === null, cls: 'bg-slate-100 text-slate-500 border-slate-300', activeCls: 'bg-slate-700 text-white border-slate-700' },
];

const slipDiff = (inv) => {
  if (!inv.original_expected_date || !inv.expected_date) return null;
  const a = new Date(inv.original_expected_date + 'T00:00:00');
  const b = new Date(inv.expected_date + 'T00:00:00');
  if (isNaN(a) || isNaN(b)) return null;
  return Math.round((b - a) / 86400000);
};
const slipBucket = (inv) => {
  const d = slipDiff(inv);
  const b = SLIP_BUCKETS.find((x) => x.match(d)) || SLIP_BUCKETS[0];
  // Chip shows the raw numeric slip so users see the exact days at a glance;
  // the coloured pill still communicates the bucket, and the tooltip has the label.
  const chipLabel = d === null ? '—' : d === 0 ? '0d' : d < 0 ? `${d}d` : `+${d}d`;
  return { ...b, diff: d, chipLabel };
};
const isSlipped = (inv) => {
  const d = slipDiff(inv);
  return d !== null && d > 0;
};

const GROUPS = [['day', 'Day'], ['salesperson', 'Salesperson'], ['client_type', 'Client Type']];
// IST-normalised "today + N" — deployed backend runs UTC and browser may not
// be IST either. Business runs on India clock so we force Asia/Kolkata.
const isoAdd = (d) => {
  const dt = new Date();
  dt.setDate(dt.getDate() + d);
  return dt.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};
const pad2 = (n) => String(n).padStart(2, '0');
const monthRange = (ym) => {
  const [y, m] = ym.split('-').map(Number);
  const eom = new Date(y, m, 0).getDate();
  return { from_date: `${y}-${pad2(m)}-01`, to_date: `${y}-${pad2(m)}-${pad2(eom)}` };
};
const quarterRange = (yq) => {
  // yq = "2026-Q1" (Apr-Jun in FY calendar). Q1: Apr-Jun, Q2: Jul-Sep, Q3: Oct-Dec, Q4: Jan-Mar (following calendar year)
  const [y, qStr] = yq.split('-Q');
  const q = Number(qStr);
  const fyStart = Number(y);
  const startMonth = [4, 7, 10, 1][q - 1];
  const yearForStart = q === 4 ? fyStart + 1 : fyStart;
  const endMonth = startMonth + 2;
  const endYear = yearForStart;
  const eom = new Date(endYear, endMonth, 0).getDate();
  return { from_date: `${yearForStart}-${pad2(startMonth)}-01`, to_date: `${endYear}-${pad2(endMonth)}-${pad2(eom)}` };
};
const fyRange = (fy) => {
  // fy = 2026 → 1-Apr-2026 to 31-Mar-2027
  return { from_date: `${fy}-04-01`, to_date: `${fy + 1}-03-31` };
};
const currentFy = () => {
  const d = new Date();
  return d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
};
const PRESETS = [
  { id: 'today', label: 'Today', params: () => ({ from_date: isoAdd(0), to_date: isoAdd(0) }) },
  { id: 'tomorrow', label: 'Tmrw', params: () => ({ from_date: isoAdd(1), to_date: isoAdd(1) }) },
  { id: 'next7', label: '7d', params: () => ({ days: 7 }) },
  { id: 'next10', label: '10d', params: () => ({ days: 10 }) },
  { id: 'next15', label: '15d', params: () => ({ days: 15 }) },
  { id: 'next30', label: '30d', params: () => ({ days: 30 }) },
];

export const CollectionPlanTrackerModal = ({ open, onOpenChange, zohoOrgId }) => {
  const { user } = useAuth();
  const isAdmin = (user?.role || '').toLowerCase() === 'admin';
  const [preset, setPreset] = useState('next10');
  const [custom, setCustom] = useState(false);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [monthPick, setMonthPick] = useState('');            // "2026-09"
  const [quarterPick, setQuarterPick] = useState('');        // "2026-Q2"
  const [fyPick, setFyPick] = useState('');                  // "2026"
  const [groupBy, setGroupBy] = useState('day');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hideZeroDue, setHideZeroDue] = useState(false);
  const [includeOverdue, setIncludeOverdue] = useState(true);
  const [excludeClientType, setExcludeClientType] = useState('');
  const [excludeSalespersons, setExcludeSalespersons] = useState(['Deb']);  // Deb excluded by default; user can uncheck via picker
  const [includeBatch, setIncludeBatch] = useState('');           // Batch number filter
  const [spMenuOpen, setSpMenuOpen] = useState(false);
  const [slipFilter, setSlipFilter] = useState('all'); // 'all' or one of SLIP_BUCKETS keys
  const [expanded, setExpanded] = useState({});
  const [backfilling, setBackfilling] = useState(false);
  const [backfillState, setBackfillState] = useState(null);
  const [reasonModal, setReasonModal] = useState(null); // { invoice, loading, reasons }
  const [explainerOpen, setExplainerOpen] = useState(false);
  const [kpiFilter, setKpiFilter] = useState('all'); // 'all' | 'payments' | 'cn' | 'due' | 'slipped'
  const [detailInvoiceId, setDetailInvoiceId] = useState(null);

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      let params;
      if (monthPick) params = monthRange(monthPick);
      else if (quarterPick) params = quarterRange(quarterPick);
      else if (fyPick) params = fyRange(Number(fyPick));
      else if (custom && customFrom && customTo) params = { from_date: customFrom, to_date: customTo };
      else params = (PRESETS.find((p) => p.id === preset) || PRESETS[3]).params();
      if (includeBatch) params.include_batch = includeBatch;
      const r = await api.get('/books/collection-plan', { params });
      setData(r.data);
      setExpanded({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load collection plan');
    } finally {
      setLoading(false);
    }
  }, [preset, custom, customFrom, customTo, monthPick, quarterPick, fyPick, includeBatch]);

  useEffect(() => { if (open) fetchPlan(); }, [open, fetchPlan]);

  const filteredInvoices = useMemo(() => {
    if (!data) return [];
    // Merge overdue rows into the main list when the toggle is on. Each overdue
    // row already has days_late set by the backend; we tag with `is_overdue`
    // so the UI can style it distinctly (red stripe + "N d overdue" chip).
    let arr = data.invoices.map((r) => ({ ...r, is_overdue: false }));
    if (includeOverdue) {
      const od = (data.overdue || []).map((r) => ({ ...r, is_overdue: true }));
      arr = [...od, ...arr];
    }
    if (excludeClientType) arr = arr.filter((i) => (i.client_type || '') !== excludeClientType);
    if (excludeSalespersons.length) arr = arr.filter((i) => !excludeSalespersons.includes(i.salesperson_name || ''));
    if (hideZeroDue) arr = arr.filter((i) => (i.still_due || 0) > 0.01);
    if (slipFilter !== 'all') {
      const b = SLIP_BUCKETS.find((x) => x.key === slipFilter);
      if (b) arr = arr.filter((i) => b.match(slipDiff(i)));
    }
    // KPI-card click filters: narrow the row list to invoices that
    // contribute to the clicked KPI so operators can drill straight in.
    if (kpiFilter === 'payments')  arr = arr.filter((i) => (i.received_window || 0) > 0.01);
    else if (kpiFilter === 'cn')   arr = arr.filter((i) => (i.credit_notes_window || 0) > 0.01);
    else if (kpiFilter === 'due')  arr = arr.filter((i) => (i.still_due || 0) > 0.01);
    else if (kpiFilter === 'slipped') arr = arr.filter((i) => { const d = slipDiff(i); return d !== null && d > 0; });
    return arr;
  }, [data, hideZeroDue, slipFilter, kpiFilter, excludeClientType, excludeSalespersons, includeOverdue]);

  // Client types: fixed canonical list requested by ops (None, Retail, Corporate).
  // Also merge in any distinct value found in the current window so nothing
  // silently vanishes if Zoho ever holds a new value.
  const clientTypes = useMemo(() => {
    const canonical = ['Retail', 'Corporate'];
    if (!data) return canonical;
    const found = new Set(data.invoices.map((i) => i.client_type || '').filter(Boolean));
    canonical.forEach((c) => found.add(c));
    return [...found].sort();
  }, [data]);

  const salespersons = useMemo(() => {
    if (!data) return [];
    const base = [...(data.overdue || []), ...data.invoices];
    return [...new Set(base.map((i) => i.salesperson_name || '').filter(Boolean))].sort();
  }, [data]);

  // Bucket counts computed from the whole window (before slipFilter, after hideZeroDue + client-type exclude)
  // so users see how many invoices fall into each bucket at a glance.
  const bucketStats = useMemo(() => {
    if (!data) return {};
    let base = includeOverdue ? [...(data.overdue || []), ...data.invoices] : data.invoices;
    if (excludeClientType) base = base.filter((i) => (i.client_type || '') !== excludeClientType);
    if (excludeSalespersons.length) base = base.filter((i) => !excludeSalespersons.includes(i.salesperson_name || ''));
    if (hideZeroDue) base = base.filter((i) => (i.still_due || 0) > 0.01);
    const stats = {};
    for (const b of SLIP_BUCKETS) {
      const matched = base.filter((i) => b.match(slipDiff(i)));
      stats[b.key] = { count: matched.length, due: matched.reduce((a, r) => a + (r.still_due || 0), 0) };
    }
    return stats;
  }, [data, hideZeroDue, excludeClientType, excludeSalespersons, includeOverdue]);

  const openReasonModal = async (inv) => {
    setReasonModal({ invoice: inv, loading: true, reasons: [] });
    try {
      const r = await api.get(`/books/invoice/${inv.invoice_id}/expected-payment-reasons`);
      setReasonModal({ invoice: inv, loading: false, reasons: r.data.reasons || [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load reasons');
      setReasonModal({ invoice: inv, loading: false, reasons: [], error: true });
    }
  };

  const slippedSummary = useMemo(() => {
    if (!data) return { count: 0, due: 0, noBaseline: 0 };
    let base = data.invoices;
    if (excludeClientType) base = base.filter((i) => (i.client_type || '') !== excludeClientType);
    if (excludeSalespersons.length) base = base.filter((i) => !excludeSalespersons.includes(i.salesperson_name || ''));
    const slipped = base.filter(isSlipped);
    return {
      count: slipped.length,
      due: slipped.reduce((a, r) => a + (r.still_due || 0), 0),
      noBaseline: base.filter((i) => !i.original_expected_date).length,
    };
  }, [data, excludeClientType, excludeSalespersons]);

  const groups = useMemo(() => {
    if (!data) return [];
    const keyOf = (inv) => groupBy === 'day' ? inv.expected_date
      : groupBy === 'salesperson' ? inv.salesperson_name
        : groupBy === 'client_type' ? inv.client_type : inv.customer_name;
    const m = new Map();
    for (const inv of filteredInvoices) {
      const k = keyOf(inv) || '(Unknown)';
      if (!m.has(k)) m.set(k, { key: k, invoices: [], promised: 0, received: 0, due: 0 });
      const g = m.get(k);
      g.invoices.push(inv);
      g.promised += inv.promised; g.received += inv.received_window; g.due += inv.still_due;
    }
    const arr = [...m.values()];
    if (groupBy === 'day') arr.sort((a, b) => a.key.localeCompare(b.key));
    else arr.sort((a, b) => b.due - a.due);
    return arr;
  }, [filteredInvoices, groupBy, data]);

  const triggerBackfill = async () => {
    if (!window.confirm('This will re-fetch invoice details from Zoho Books to populate Original Expected Payment Date. It runs in the background and may take several minutes. Continue?')) return;
    try {
      setBackfilling(true);
      const r = await api.post('/books/backfill-original-expected-date');
      toast.success(r.data.message || 'Backfill started');
      pollBackfill();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start backfill');
      setBackfilling(false);
    }
  };

  const pollBackfill = async () => {
    try {
      const r = await api.get('/books/backfill-original-expected-date/status');
      setBackfillState(r.data);
      if (r.data.running) setTimeout(pollBackfill, 4000);
      else {
        setBackfilling(false);
        if (r.data.updated > 0) {
          toast.success(`Backfill done — updated ${r.data.updated} invoice(s)`);
          fetchPlan();
        }
      }
    } catch {
      setBackfilling(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const inr = (n) => Math.round(n || 0);
    const lines = [];
    lines.push(esc('Collection Plan — Slip Tracker'));
    lines.push(esc(`Window: ${data.window.from} to ${data.window.to}`));
    lines.push(esc(`Grouped by: ${GROUPS.find(([v]) => v === groupBy)?.[1] || groupBy}`));
    if (hideZeroDue) lines.push(esc('Filter: Hide 0-due invoices'));
    if (excludeClientType) lines.push(esc(`Filter: Excluding client type = ${excludeClientType}`));
    if (excludeSalespersons.length) lines.push(esc(`Filter: Excluding salespersons = ${excludeSalespersons.join(', ')}`));
    if (slipFilter !== 'all') lines.push(esc(`Filter: Slip bucket = ${SLIP_BUCKETS.find((b) => b.key === slipFilter)?.label}`));
    lines.push(esc(`Generated: ${new Date().toLocaleString('en-IN')}`));
    lines.push('');
    const totP = filteredInvoices.reduce((a, r) => a + (r.promised || 0), 0);
    const totR = filteredInvoices.reduce((a, r) => a + (r.received_window || 0), 0);
    const totD = filteredInvoices.reduce((a, r) => a + (r.still_due || 0), 0);
    const totPct = totP > 0 ? Math.round(totR / totP * 100) : 0;
    lines.push(['KPI', 'Value', 'Notes'].map(esc).join(','));
    lines.push(['Promised (window)', inr(totP), `${filteredInvoices.length} invoice(s)`].map(esc).join(','));
    lines.push(['Received (in window)', inr(totR), `${totPct}% of promised`].map(esc).join(','));
    lines.push(['Expected Collection (Due)', inr(totD), 'Outstanding due as of today'].map(esc).join(','));
    lines.push(['Slipped promises (any)', inr(slippedSummary.due), `${slippedSummary.count} invoice(s) — Current > Original`].map(esc).join(','));
    lines.push('');
    // Bucket breakdown for context
    lines.push(['Slip Bucket', 'Invoice count', 'Due (Rs)'].map(esc).join(','));
    SLIP_BUCKETS.forEach((b) => {
      const st = bucketStats[b.key] || { count: 0, due: 0 };
      lines.push([b.label, st.count, inr(st.due)].map(esc).join(','));
    });
    lines.push('');
    const headers = ['S.No', 'Group', 'Original Expected', 'Current Expected', 'Slip (days)', 'Slip Bucket', 'Invoice #', 'Enrolment #', 'Customer', 'Salesperson', 'Client Type', 'Received (window)', 'Last Received', 'Due (Expected)', 'Status'];
    lines.push(headers.map(esc).join(','));
    let sn = 0;
    const emit = (r, group) => {
      sn += 1;
      const b = slipBucket(r);
      lines.push([sn, group || '', r.original_expected_date || '', r.expected_date || '', b.diff ?? '', b.label, r.invoice_number, r.enrolment_no || '', r.customer_name, r.salesperson_name || '', r.client_type || '', inr(r.received_window) || '', r.last_received_date || '', inr(r.still_due), r.state || (r.still_due > 0 ? 'open' : 'done')].map(esc).join(','));
    };
    groups.forEach((g) => {
      const label = groupBy === 'day' ? dayLabel(g.key) : g.key;
      g.invoices.forEach((inv) => emit(inv, label));
    });
    lines.push('');
    lines.push(['', '', '', '', '', 'GRAND TOTAL', '', '', `${filteredInvoices.length} invoice(s)`, '', '', inr(totR), '', inr(totD), `${totPct}% received`].map(esc).join(','));
    const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `collection-slip-tracker-${data.window.from}-to-${data.window.to}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const exportPdf = async () => {
    if (!data) return;
    const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([import('jspdf'), import('jspdf-autotable')]);
    const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();
    const margin = 24;
    const inrTxt = (n) => `Rs. ${Math.round(n || 0).toLocaleString('en-IN')}`;
    const groupLabel = GROUPS.find(([v]) => v === groupBy)?.[1] || groupBy;

    doc.setFillColor(30, 41, 59);
    doc.rect(0, 0, pageW, 56, 'F');
    doc.setFillColor(244, 63, 94);
    doc.rect(0, 56, pageW, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(16);
    doc.text('Collection Plan — Slip Tracker', margin, 28);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.setTextColor(203, 213, 225);
    const filters = [
      hideZeroDue ? 'Hide 0-due' : null,
      excludeClientType ? `Excl. ${excludeClientType}` : null,
      excludeSalespersons.length ? `Excl. SP: ${excludeSalespersons.join(' / ')}` : null,
      slipFilter !== 'all' ? `Slip = ${SLIP_BUCKETS.find((b) => b.key === slipFilter)?.label}` : null,
    ].filter(Boolean).join(', ');
    doc.text(`${data.window.from}  to  ${data.window.to}   |   Grouped by: ${groupLabel}${filters ? '   |   Filters: ' + filters : ''}`, margin, 46);
    doc.setFontSize(8);
    doc.text(`Generated ${new Date().toLocaleString('en-IN')}`, pageW - margin, 46, { align: 'right' });

    const kpiY = 74;
    const kpiH = 52;
    const gap = 8;
    const kpiW = (pageW - margin * 2 - gap * 4) / 5;
    const kTotP = filteredInvoices.reduce((a, r) => a + (r.promised || 0), 0);
    const kTotR = filteredInvoices.reduce((a, r) => a + (r.received_window || 0), 0);
    const kTotD = filteredInvoices.reduce((a, r) => a + (r.still_due || 0), 0);
    const kTotPct = kTotP > 0 ? Math.round(kTotR / kTotP * 100) : 0;
    const kpis = [
      { label: 'PROMISED (WINDOW)', value: inrTxt(kTotP), sub: `${filteredInvoices.length} invoice(s)`, fill: [219, 234, 254], border: [59, 130, 246], text: [30, 58, 138] },
      { label: 'RECEIVED', value: inrTxt(kTotR), sub: `${kTotPct}% of promised`, fill: [209, 250, 229], border: [16, 185, 129], text: [6, 78, 59] },
      { label: 'EXPECTED (DUE)', value: inrTxt(kTotD), sub: 'Outstanding as of today', fill: [254, 243, 199], border: [245, 158, 11], text: [120, 53, 15] },
      { label: 'SLIPPED PROMISES', value: inrTxt(slippedSummary.due), sub: `${slippedSummary.count} inv — Current > Original`, fill: [254, 226, 226], border: [244, 63, 94], text: [127, 29, 29] },
      { label: 'NO BASELINE', value: `${slippedSummary.noBaseline} inv`, sub: 'Original date not captured', fill: [241, 245, 249], border: [148, 163, 184], text: [51, 65, 85] },
    ];
    kpis.forEach((k, i) => {
      const x = margin + i * (kpiW + gap);
      doc.setFillColor(...k.fill);
      doc.setDrawColor(...k.border);
      doc.setLineWidth(0.8);
      doc.roundedRect(x, kpiY, kpiW, kpiH, 4, 4, 'FD');
      doc.setTextColor(...k.text);
      doc.setFont('helvetica', 'bold'); doc.setFontSize(6.5);
      doc.text(k.label, x + 6, kpiY + 11);
      doc.setFontSize(11);
      doc.text(k.value, x + 6, kpiY + 28);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
      doc.setTextColor(71, 85, 105);
      doc.text(k.sub, x + 6, kpiY + 44, { maxWidth: kpiW - 12 });
    });

    const noteY = kpiY + kpiH + 8;
    doc.setFillColor(255, 251, 235);
    doc.setDrawColor(252, 211, 77);
    doc.roundedRect(margin, noteY, pageW - margin * 2, 18, 3, 3, 'FD');
    doc.setTextColor(120, 53, 15);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
    doc.text('Slip = Current Expected Payment Date − Original Expected Payment Date. Positive means the promise date has moved later. Rows with no captured original show "No baseline".', margin + 8, noteY + 12);

    const head = [['#', 'Original', 'Current', 'Slip', 'Invoice #', 'Enrol', 'Customer', 'Salesperson', 'Received', 'Due', 'Status']];
    const body = [];
    let sn = 0;
    const rowFor = (r) => {
      sn += 1;
      const b = slipBucket(r);
      return [sn, r.original_expected_date || '-', r.expected_date || '-', b.chipLabel, r.invoice_number, r.enrolment_no || '-', r.customer_name || '', r.salesperson_name || '', r.received_window ? Math.round(r.received_window).toLocaleString('en-IN') : '-', Math.round(r.still_due).toLocaleString('en-IN'), r.state || (r.still_due > 0 ? 'open' : 'done')];
    };

    groups.forEach((g) => {
      const label = groupBy === 'day' ? dayLabel(g.key) : g.key;
      const pct = g.promised > 0 ? Math.round(g.received / g.promised * 100) : 0;
      body.push([{ content: `${label}   -   ${g.invoices.length} inv   |   Promised ${Math.round(g.promised).toLocaleString('en-IN')}   |   Received ${Math.round(g.received).toLocaleString('en-IN')} (${pct}%)   |   Due ${Math.round(g.due).toLocaleString('en-IN')}`, colSpan: 11, styles: { fillColor: [224, 242, 254], textColor: [12, 74, 110], fontStyle: 'bold', fontSize: 9, cellPadding: 5 } }]);
      g.invoices.forEach((inv) => body.push(rowFor(inv)));
    });
    body.push([
      { content: `GRAND TOTAL - ${filteredInvoices.length} invoice(s)`, colSpan: 8, styles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold', halign: 'right', fontSize: 10 } },
      { content: Math.round(kTotR).toLocaleString('en-IN'), styles: { fillColor: [30, 41, 59], textColor: [134, 239, 172], fontStyle: 'bold', halign: 'right', fontSize: 10 } },
      { content: Math.round(kTotD).toLocaleString('en-IN'), styles: { fillColor: [30, 41, 59], textColor: [253, 224, 71], fontStyle: 'bold', halign: 'right', fontSize: 10 } },
      { content: `${kTotPct}%`, styles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 10 } },
    ]);

    autoTable(doc, {
      startY: noteY + 26,
      head, body,
      margin: { left: margin, right: margin, bottom: 28 },
      styles: { fontSize: 7.5, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
      headStyles: { fillColor: [244, 63, 94], textColor: 255, fontStyle: 'bold', halign: 'center' },
      columnStyles: {
        0: { halign: 'right', cellWidth: 22 },
        1: { cellWidth: 56 },
        2: { cellWidth: 56 },
        3: { cellWidth: 46, halign: 'center' },
        4: { cellWidth: 68 },
        5: { cellWidth: 40 },
        6: { cellWidth: 170 },
        7: { cellWidth: 84 },
        8: { halign: 'right', cellWidth: 60 },
        9: { halign: 'right', cellWidth: 62 },
        10: { halign: 'center', cellWidth: 58 },
      },
      alternateRowStyles: { fillColor: [249, 250, 251] },
      didDrawPage: () => {
        doc.setFontSize(7); doc.setTextColor(148, 163, 184);
        doc.text('Collection Plan — Slip Tracker  |  Confidential', margin, pageH - 12);
        doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageW - margin, pageH - 12, { align: 'right' });
      },
    });
    doc.save(`collection-slip-tracker-${data.window.from}-to-${data.window.to}.pdf`);
  };

  const invRow = (inv, key, sn) => {
    const b = slipBucket(inv);
    return (
      <div key={key}
        onClick={() => setDetailInvoiceId(inv.invoice_id)}
        data-testid={`slip-row-${inv.invoice_number}`}
        role="button" tabIndex={0}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetailInvoiceId(inv.invoice_id); } }}
        className={`grid grid-cols-[repeat(16,minmax(0,1fr))] gap-2 px-3 py-1.5 border-t border-slate-100 text-[11px] items-center hover:bg-sky-50/50 cursor-pointer
                    ${inv.is_overdue ? 'bg-rose-50/30 border-l-2 border-l-rose-500' : ''}`}>
        <div className="text-slate-400 tabular-nums flex items-center gap-1">
          {sn}
          <button type="button" onClick={(e) => { e.stopPropagation(); openReasonModal(inv); }}
            data-testid={`slip-reason-${inv.invoice_number}`}
            title="Show reason(s) for expected-date changes"
            className="text-slate-400 hover:text-violet-600 transition">
            <MessageSquare className="w-3 h-3" />
          </button>
        </div>
        <div className="col-span-2 font-medium text-slate-700 whitespace-nowrap">{inv.invoice_number}</div>
        <div className="text-[10px] text-slate-500 tabular-nums truncate" title={inv.enrolment_no || ''}>
          {inv.enrolment_no ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                window.open(`/training/students?focus=${encodeURIComponent(inv.enrolment_no)}`, '_blank');
              }}
              className="text-violet-700 hover:text-violet-900 underline decoration-dotted"
              data-testid={`slip-enrol-${inv.enrolment_no}`}
              title="Open student in Training Mgmt (new tab)">
              {inv.enrolment_no}
            </button>
          ) : '—'}
        </div>
        <div className="col-span-3 text-slate-700 truncate" title={inv.customer_name}>{inv.customer_name}</div>
        <div className="text-slate-500 whitespace-nowrap text-[10px]">{inv.original_expected_date || <span className="text-slate-300">—</span>}</div>
        <div className="text-slate-700 whitespace-nowrap text-[10px] font-medium">
          {inv.expected_date}
          {inv.is_overdue && inv.days_late != null && (
            <span className="ml-1 inline-block px-1 rounded bg-rose-100 text-rose-700 text-[9px] font-semibold">{inv.days_late}d overdue</span>
          )}
        </div>
        <div className="text-center"><span className={`inline-block px-1.5 py-0.5 rounded border text-[10px] font-semibold tabular-nums ${b.cls}`} title={inv.original_expected_date ? `${b.label}  ·  Original ${inv.original_expected_date} → Current ${inv.expected_date}` : 'No original date captured'}>{b.chipLabel}</span></div>
        <div className="col-span-2 text-right tabular-nums text-emerald-700">
          {inv.received_window > 0 ? fmtInr(inv.received_window) : '—'}
          {inv.last_received_date && <span className="text-[9px] text-slate-400 ml-1">{inv.last_received_date.slice(5)}</span>}
        </div>
        <div className={`text-right tabular-nums font-semibold ${inv.still_due > 0 ? 'text-amber-700' : 'text-emerald-700'}`}>{fmtInr(inv.still_due)}</div>
        <div className="text-center flex items-center justify-center gap-1 flex-wrap">
          <StateBadge state={inv.state || (inv.still_due > 0 ? 'open' : 'done')} />
          {inv.credit_notes_window > 0.01 && (
            <span className="inline-block text-[8.5px] font-bold px-1 py-0 rounded border border-violet-300 bg-violet-50 text-violet-700 leading-tight"
              data-testid={`slip-row-cn-flag-${inv.invoice_number}`}
              title={`Credit note applied: ${fmtInr(inv.credit_notes_window)}`}>CN</span>
          )}
        </div>
        <div className="text-slate-400 truncate text-[10px]" title={inv.salesperson_name}>{inv.salesperson_name}</div>
        <div className="text-right">
          {zohoOrgId && <a href={`https://books.zoho.com/app/${zohoOrgId}#/invoices/${inv.invoice_id}`} target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-violet-600 hover:underline text-[10px] font-medium" data-testid={`slip-inv-link-${inv.invoice_number}`}>Open ↗</a>}
        </div>
      </div>
    );
  };

  const s = data?.summary;
  const totP = filteredInvoices.reduce((a, r) => a + (r.promised || 0), 0);
  const totR = filteredInvoices.reduce((a, r) => a + (r.received_window || 0), 0);
  const totCN = filteredInvoices.reduce((a, r) => a + (r.credit_notes_window || 0), 0);
  const totD = filteredInvoices.reduce((a, r) => a + (r.still_due || 0), 0);
  const totPct = totP > 0 ? Math.round(totR / totP * 100) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="collection-slip-tracker-dialog">
        <DialogHeader>
          <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">📉 Collection Plan — Slip Tracker
            <span className="text-[10px] font-normal text-rose-600 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5">Tracks Original vs Current Expected Date</span>
            <button type="button" onClick={() => setExplainerOpen(true)} data-testid="slip-explainer-btn"
              className="ml-1 inline-flex items-center gap-1 text-[10px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 hover:bg-indigo-100 transition"
              title="Explain the KPI math and why totals may differ from Billing vs Collections">
              <Info className="w-3 h-3" /> How this maths
            </button>
          </DialogTitle>
          {data && <p className="text-[11px] text-slate-500 mt-0.5">Expected payments {data.window.from} → {data.window.to} · Slip = Current − Original promise date (from Zoho custom field)</p>}
        </DialogHeader>

        <div className="flex flex-wrap items-center gap-1.5 text-[11px]">
          {/* Compact preset pills */}
          <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md overflow-hidden">
            {PRESETS.map((pr) => (
              <button key={pr.id} type="button" onClick={() => { setCustom(false); setMonthPick(''); setQuarterPick(''); setFyPick(''); setPreset(pr.id); }}
                data-testid={`slip-preset-${pr.id}`}
                className={`px-2 py-1 border-r border-slate-200 last:border-r-0 transition ${!custom && !monthPick && !quarterPick && !fyPick && preset === pr.id ? 'bg-rose-600 text-white' : 'text-slate-600 hover:bg-white'}`}>{pr.label}</button>
            ))}
          </div>
          <button type="button" onClick={() => { setMonthPick(''); setQuarterPick(''); setFyPick(''); setCustom(true); }}
            data-testid="slip-preset-custom"
            className={`px-2 py-1 rounded-md border transition ${custom ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>Custom</button>
          {custom && (
            <span className="flex items-center gap-1">
              <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border border-slate-200 rounded-md px-1.5 py-0.5" data-testid="slip-custom-from" />
              <span className="text-slate-400 text-[10px]">→</span>
              <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border border-slate-200 rounded-md px-1.5 py-0.5" data-testid="slip-custom-to" />
            </span>
          )}
          {/* Month / Quarter / FY collapsed into one compact popover */}
          <MoreRanges
            monthPick={monthPick} quarterPick={quarterPick} fyPick={fyPick}
            setMonthPick={(v) => { setMonthPick(v); setQuarterPick(''); setFyPick(''); setCustom(false); }}
            setQuarterPick={(v) => { setQuarterPick(v); setMonthPick(''); setFyPick(''); setCustom(false); }}
            setFyPick={(v) => { setFyPick(v); setMonthPick(''); setQuarterPick(''); setCustom(false); }} />

          <span className="ml-auto flex items-center gap-1.5">
            <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer select-none" title="Hide invoices already fully collected in this window">
              <input type="checkbox" checked={hideZeroDue} onChange={(e) => setHideZeroDue(e.target.checked)} data-testid="slip-hide-zero-due"
                className="w-3 h-3 accent-rose-600" />
              <span>0-due</span>
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer select-none" title="Also include past-due unpaid invoices whose Expected Payment Date has already passed">
              <input type="checkbox" checked={includeOverdue} onChange={(e) => setIncludeOverdue(e.target.checked)} data-testid="slip-include-overdue"
                className="w-3 h-3 accent-rose-600" />
              <span>Overdue</span>
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500 select-none" title="Exclude one client type from the view">
              <span className="text-slate-400">CT</span>
              <select value={excludeClientType} onChange={(e) => setExcludeClientType(e.target.value)} data-testid="slip-exclude-client-type"
                className="text-[10px] border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-rose-400">
                <option value="">All</option>
                {clientTypes.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
              </select>
            </label>
            <div className="relative flex items-center gap-1 text-[10px] text-slate-500 select-none">
              <span className="text-slate-400">SP</span>
              <button type="button" onClick={() => setSpMenuOpen((v) => !v)} data-testid="slip-exclude-salesperson"
                className="border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-rose-400 min-w-[80px] text-left flex items-center justify-between gap-1">
                <span className="truncate max-w-[80px]">
                  {excludeSalespersons.length === 0 ? 'All'
                    : excludeSalespersons.length === 1 ? excludeSalespersons[0]
                    : `${excludeSalespersons.length} out`}
                </span>
                <ChevronDown className="w-3 h-3 shrink-0" />
              </button>
              {spMenuOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setSpMenuOpen(false)} data-testid="slip-exclude-salesperson-backdrop" />
                  <div className="absolute top-full left-14 mt-1 z-50 bg-white border border-slate-200 rounded-md shadow-lg py-1 max-h-64 overflow-auto min-w-[180px]" data-testid="slip-exclude-salesperson-menu">
                    <div className="flex items-center justify-between px-2 py-1 border-b border-slate-100 text-[9px] uppercase tracking-wide text-slate-400">
                      <span>Exclude salespersons</span>
                      {excludeSalespersons.length > 0 && (
                        <button type="button" onClick={() => setExcludeSalespersons([])} className="text-rose-600 hover:underline normal-case" data-testid="slip-exclude-salesperson-clear">
                          Clear
                        </button>
                      )}
                    </div>
                    {salespersons.length === 0 ? (
                      <p className="px-2 py-2 text-[10px] italic text-slate-400">No salespersons in window</p>
                    ) : salespersons.map((sp) => {
                      const checked = excludeSalespersons.includes(sp);
                      return (
                        <label key={sp} className="flex items-center gap-2 px-2 py-1 text-[11px] text-slate-700 hover:bg-slate-50 cursor-pointer">
                          <input type="checkbox" checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setExcludeSalespersons([...excludeSalespersons, sp]);
                              else setExcludeSalespersons(excludeSalespersons.filter((x) => x !== sp));
                            }}
                            className="w-3 h-3 accent-rose-600"
                            data-testid={`slip-exclude-salesperson-opt-${sp}`}
                          />
                          <span className="truncate">{sp}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </div>
            <label className="flex items-center gap-1 text-[10px] text-slate-500 select-none" title="Filter to a specific training batch">
              <span className="text-slate-400">Batch</span>
              <select value={includeBatch} onChange={(e) => setIncludeBatch(e.target.value)} data-testid="slip-batch-filter"
                className="text-[10px] border border-slate-200 rounded-md px-1.5 py-0.5 bg-white text-slate-700 focus:outline-none focus:ring-1 focus:ring-rose-400 min-w-[70px]">
                <option value="">All</option>
                {(data?.batches || []).map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </label>
            <span className="text-slate-400 ml-1">Group</span>
            <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md overflow-hidden">
              {GROUPS.map(([v, l]) => (
                <button key={v} type="button" onClick={() => setGroupBy(v)} data-testid={`slip-group-${v}`}
                  className={`px-2 py-1 border-r border-slate-200 last:border-r-0 transition ${groupBy === v ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-white'}`}>{l}</button>
              ))}
            </div>
            {/* Consolidated tools menu */}
            <ToolsMenu
              onExportCsv={exportCsv}
              onExportPdf={exportPdf}
              onBackfill={triggerBackfill}
              backfilling={backfilling}
              isAdmin={isAdmin}
            />
          </span>
        </div>

        {backfillState && backfillState.total > 0 && (
          <div className="text-[10px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2.5 py-1.5" data-testid="slip-backfill-status">
            Backfill: {backfillState.done}/{backfillState.total} processed · {backfillState.updated} updated · {backfillState.errors} errors {backfillState.running ? '(running…)' : '(done)'}
            {backfillState.last_error && <span className="ml-2 text-rose-600">Last error: {backfillState.last_error}</span>}
          </div>
        )}

        {s && (
          <div className="rounded-md border border-indigo-200 bg-indigo-50/60 px-2.5 py-1.5 text-[11px] text-indigo-900" data-testid="slip-overview-hint">
            <b>How to read this window:</b> Every card below shows the same set of invoices — those with an <b>expected payment date</b> inside your chosen range. <b>Promised</b> = <b>Actual Payments</b> + <b>Expected Due</b>. Credit Notes are shown separately (they clear balances but aren't cash). Click a card to filter the list below.
          </div>
        )}
        {s && (
          <div className="grid grid-cols-5 gap-3" data-testid="slip-summary">
            <button type="button" onClick={() => setKpiFilter((f) => f === 'all' ? 'all' : 'all')}
              data-testid="slip-kpi-promised"
              title="Reset to all invoices in this window"
              className={`text-left transition-all rounded-lg px-3 py-2 border ${kpiFilter === 'all' ? 'bg-sky-100 border-sky-300 ring-2 ring-sky-300' : 'bg-sky-50 border-sky-100 hover:bg-sky-100'}`}>
              <p className="text-[9px] uppercase tracking-wide text-sky-600 font-semibold">Promised this window · click to reset</p>
              <p className="text-sm font-bold text-sky-800 tabular-nums">{fmtInr(totP)} <span className="text-[10px] font-normal text-slate-500">· {filteredInvoices.length} inv</span></p>
            </button>
            <button type="button" onClick={() => setKpiFilter((f) => f === 'payments' ? 'all' : 'payments')}
              data-testid="slip-kpi-payments"
              title="Show only invoices with actual cash payments received in this window"
              className={`text-left transition-all rounded-lg px-3 py-2 border ${kpiFilter === 'payments' ? 'bg-emerald-100 border-emerald-300 ring-2 ring-emerald-300' : 'bg-emerald-50 border-emerald-100 hover:bg-emerald-100'}`}>
              <p className="text-[9px] uppercase tracking-wide text-emerald-600 font-semibold">Actual payments · click to filter</p>
              <p className="text-sm font-bold text-emerald-800 tabular-nums">{fmtInr(totR)} <span className="text-[10px] font-normal text-slate-500">· {totPct}%</span></p>
              <p className="text-[9px] text-emerald-700/70 leading-tight">Cash-in-hand (payments dated in window)</p>
            </button>
            <button type="button" onClick={() => setKpiFilter((f) => f === 'cn' ? 'all' : 'cn')}
              data-testid="slip-kpi-cn"
              title="Show only invoices with a credit note applied on or before window end"
              className={`text-left transition-all rounded-lg px-3 py-2 border ${kpiFilter === 'cn' ? 'bg-violet-100 border-violet-300 ring-2 ring-violet-300' : 'bg-violet-50 border-violet-100 hover:bg-violet-100'}`}>
              <p className="text-[9px] uppercase tracking-wide text-violet-600 font-semibold">Credit notes applied · click to filter</p>
              <p className="text-sm font-bold text-violet-800 tabular-nums">{fmtInr(totCN)}</p>
              <p className="text-[9px] text-violet-700/70 leading-tight">Accounting offset, not cash</p>
            </button>
            <button type="button" onClick={() => setKpiFilter((f) => f === 'due' ? 'all' : 'due')}
              data-testid="slip-kpi-due"
              title="Show only invoices with outstanding balance still due"
              className={`text-left transition-all rounded-lg px-3 py-2 border ${kpiFilter === 'due' ? 'bg-amber-100 border-amber-300 ring-2 ring-amber-300' : 'bg-amber-50 border-amber-100 hover:bg-amber-100'}`}>
              <p className="text-[9px] uppercase tracking-wide text-amber-600 font-semibold">Expected Collection (Due) · click to filter</p>
              <p className="text-sm font-bold text-amber-800 tabular-nums">{fmtInr(totD)}</p>
            </button>
            <button type="button" onClick={() => setKpiFilter((f) => f === 'slipped' ? 'all' : 'slipped')}
              data-testid="slip-kpi-slipped"
              title="Show only slipped invoices (current expected date > original)"
              className={`text-left transition-all rounded-lg px-3 py-2 border ${kpiFilter === 'slipped' ? 'bg-rose-100 border-rose-300 ring-2 ring-rose-300' : 'bg-rose-50 border-rose-100 hover:bg-rose-100'}`}>
              <p className="text-[9px] uppercase tracking-wide text-rose-600 font-semibold">Slipped promises · click to filter</p>
              <p className="text-sm font-bold text-rose-800 tabular-nums">{fmtInr(slippedSummary.due)} <span className="text-[10px] font-normal text-slate-500">· {slippedSummary.count} inv</span></p>
              <p className="text-[9px] text-rose-600/80 mt-0.5 leading-tight">Current date &gt; Original date</p>
            </button>
          </div>
        )}
        {kpiFilter !== 'all' && (
          <div className="text-[11px] flex items-center gap-2 -mt-1" data-testid="slip-kpi-filter-banner">
            <span className="text-slate-500">Filtered by KPI:</span>
            <span className="px-2 py-0.5 rounded-full bg-indigo-100 border border-indigo-200 text-indigo-700 font-semibold">
              {kpiFilter === 'payments' ? 'Actual payments only' : kpiFilter === 'cn' ? 'Credit notes only' : kpiFilter === 'due' ? 'Outstanding due only' : 'Slipped only'}
              &nbsp;· {filteredInvoices.length} inv
            </span>
            <button type="button" onClick={() => setKpiFilter('all')} data-testid="slip-kpi-clear"
              className="text-indigo-600 hover:underline">Clear</button>
          </div>
        )}

        {/* Slip bucket filter chips */}
        {s && (
          <div className="flex flex-wrap items-center gap-1.5" data-testid="slip-bucket-filters">
            <span className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold mr-1">Slip filter:</span>
            <button type="button" onClick={() => setSlipFilter('all')} data-testid="slip-bucket-all"
              className={`text-[10px] px-2 py-1 rounded-full border transition ${slipFilter === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white border-slate-300 text-slate-600 hover:bg-slate-50'}`}>
              All <span className="ml-1 opacity-70">{filteredInvoices.length + (slipFilter === 'all' ? 0 : 0)}</span>
            </button>
            {SLIP_BUCKETS.map((b) => {
              const st = bucketStats[b.key] || { count: 0 };
              const active = slipFilter === b.key;
              return (
                <button key={b.key} type="button"
                  onClick={() => setSlipFilter(active ? 'all' : b.key)}
                  data-testid={`slip-bucket-${b.key}`}
                  disabled={st.count === 0}
                  title={active ? 'Click again to clear filter' : `Show only invoices in the ${b.label} bucket`}
                  className={`text-[10px] px-2 py-1 rounded-full border transition ${active ? b.activeCls : b.cls} ${st.count === 0 ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-90'}`}>
                  {b.label} <span className="ml-1 font-semibold">{st.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {s && (
          <p className="text-[10px] text-rose-700 bg-rose-50 border border-rose-100 rounded-md px-2.5 py-1.5 leading-relaxed" data-testid="slip-note">
            <b>Slip</b> = Current Expected Payment Date − Original Expected Payment Date (both from Zoho Books). Positive means the customer moved the promise later. Click a bucket chip above to filter. Rows with no captured original show <span className="mx-0.5 px-1 rounded bg-slate-100 text-slate-500">No baseline</span> — run <b>Backfill originals</b> to populate.
          </p>
        )}

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-3">
          {loading ? (
            <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-rose-600" /></div>
          ) : !data ? null : (
            <>
              {groups.length === 0 ? (
                <p className="text-xs text-slate-400 py-10 text-center">{slipFilter !== 'all' ? `No invoices in the ${SLIP_BUCKETS.find((b) => b.key === slipFilter)?.label} bucket.` : (hideZeroDue ? 'No invoices with a pending due amount in this window.' : 'No invoices with an expected payment date in this window.')}</p>
              ) : (
                <>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 mb-1.5">
                    <span><b>{groups.length}</b> {groups.length === 1 ? 'group' : 'groups'} · click a row for details</span>
                    <span className="flex items-center gap-1.5">
                      <button type="button" onClick={() => setExpanded({})} data-testid="slip-expand-all"
                        className="px-2 py-1 rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 flex items-center gap-1">
                        <ChevronDown className="w-3 h-3" /> Expand all
                      </button>
                      <button type="button" onClick={() => setExpanded(Object.fromEntries(groups.map((g) => [g.key, false])))} data-testid="slip-collapse-all"
                        className="px-2 py-1 rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 flex items-center gap-1">
                        <ChevronDown className="w-3 h-3 rotate-180" /> Collapse all
                      </button>
                    </span>
                  </div>
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="grid grid-cols-[repeat(16,minmax(0,1fr))] gap-2 px-3 py-1.5 bg-slate-50 text-[9px] uppercase tracking-wider text-slate-500 font-medium sticky top-0 z-10">
                    <div>#</div><div className="col-span-2">Inv #</div><div>Enrol</div><div className="col-span-3">Customer</div>
                    <div>Original</div><div>Current</div><div className="text-center">Slip</div>
                    <div className="col-span-2 text-right">Received (window)</div>
                    <div className="text-right">Due (Expected)</div><div className="text-center">Status</div><div>Salesperson</div><div />
                  </div>
                  {groups.map((g, gi) => {
                    const pct = g.promised > 0 ? Math.round(g.received / g.promised * 100) : 0;
                    const isOpen = expanded[g.key] !== false;
                    const offset = groups.slice(0, gi).reduce((a, x) => a + x.invoices.length, 0);
                    return (
                      <React.Fragment key={g.key}>
                        <button type="button" onClick={() => setExpanded((p) => ({ ...p, [g.key]: !isOpen }))}
                          data-testid={`slip-grp-${g.key}`}
                          className="w-full px-3 py-2 bg-sky-50/70 border-t border-sky-100 text-left hover:bg-sky-100/60 transition">
                          <div className="flex items-center gap-2 text-[11px]">
                            <ChevronDown className={`w-3 h-3 shrink-0 text-sky-500 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                            <span className="font-semibold text-sky-900">{groupBy === 'day' ? dayLabel(g.key) : g.key}</span>
                            <span className="text-slate-500">· {g.invoices.length} inv</span>
                            <span className="ml-auto tabular-nums text-sky-800">Promised {fmtInr(g.promised)}</span>
                            <span className="tabular-nums text-emerald-700">Received {fmtInr(g.received)}</span>
                            <span className="tabular-nums text-amber-700 font-semibold">Due {fmtInr(g.due)}</span>
                            <span className="w-24 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                              <span className="block h-full bg-emerald-500 rounded-full" style={{ width: `${Math.min(pct, 100)}%` }} />
                            </span>
                            <span className="text-[10px] text-slate-500 w-8 text-right">{pct}%</span>
                          </div>
                        </button>
                        {isOpen && g.invoices.map((inv, ii) => invRow(inv, `${g.key}-${inv.invoice_id}`, offset + ii + 1))}
                      </React.Fragment>
                    );
                  })}
                  <div className="grid grid-cols-[repeat(16,minmax(0,1fr))] gap-2 px-3 py-2 bg-slate-50 border-t-2 border-slate-300 text-[11px] items-center font-bold" data-testid="slip-total-row">
                    <div className="col-span-10 text-slate-800">GRAND TOTAL — {filteredInvoices.length} invoice(s) · Promised {fmtInr(totP)}</div>
                    <div className="col-span-2 text-right tabular-nums text-emerald-700">{fmtInr(totR)}</div>
                    <div className="text-right tabular-nums text-amber-700">{fmtInr(totD)}</div>
                    <div className="text-center text-[10px] text-slate-500">{totPct}%</div>
                    <div className="col-span-2" />
                  </div>
                </div>
                </>
              )}
            </>
          )}
        </div>
      </DialogContent>

      {/* Reason modal — opens when user clicks the speech-bubble icon on any row */}
      <Dialog open={!!reasonModal} onOpenChange={(o) => { if (!o) setReasonModal(null); }}>
        <DialogContent className="max-w-2xl" data-testid="slip-reason-dialog">
          <DialogHeader>
            <DialogTitle className="text-sm font-semibold text-slate-900 flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-violet-600" />
              Expected-date change reasons — {reasonModal?.invoice?.invoice_number}
            </DialogTitle>
            <p className="text-[11px] text-slate-500 mt-0.5">{reasonModal?.invoice?.customer_name}</p>
          </DialogHeader>
          {reasonModal?.loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-violet-600" /></div>
          ) : reasonModal?.reasons?.length ? (
            <div className="space-y-2 max-h-[60vh] overflow-y-auto">
              {reasonModal.reasons.map((r, i) => (
                <div key={i} className="border border-slate-200 rounded-lg p-3 bg-slate-50/50">
                  <div className="flex items-center gap-2 text-[10px] text-slate-500 mb-1">
                    <span className="font-semibold text-slate-700">{r.date}</span>
                    {r.commented_by && r.commented_by !== '(unknown)' && <span>· by {r.commented_by}</span>}
                  </div>
                  <p className="text-xs font-medium text-violet-700">{r.change_summary}</p>
                  {r.reason && <p className="text-xs text-slate-700 mt-1 whitespace-pre-wrap">{r.reason}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-slate-400 py-8 text-center">
              No expected-date change reasons have been logged in Zoho for this invoice.
              <br />
              <span className="text-[10px] text-slate-400">(Reasons are captured when someone changes the Expected Payment Date via Zoho&apos;s Follow-up feature.)</span>
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* ── How-this-maths explainer popup ──────────────────────── */}
      <Dialog open={explainerOpen} onOpenChange={setExplainerOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="slip-explainer-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Info className="w-4 h-4 text-indigo-600" /> How the Slip Tracker maths works
            </DialogTitle>
            <p className="text-[11px] text-slate-500 mt-0.5">A quick tour so every number on this screen is unambiguous.</p>
          </DialogHeader>

          <div className="space-y-4 text-[12px] text-slate-700 leading-relaxed">
            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" /> The core identity
              </h3>
              <div className="bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2 font-mono text-[13px] text-emerald-900 tabular-nums">
                Promised &nbsp;=&nbsp; Actual&nbsp;Payments &nbsp;+&nbsp; Expected&nbsp;Due
              </div>
              <p className="text-[11px] text-slate-600 mt-1.5">
                Percentage on the Payments card = <b>Payments ÷ Promised</b>. Everything else on the strip is a decoration around this identity.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-violet-500" /> Where does <b>Credit Notes Applied</b> sit?
              </h3>
              <p>
                CN is an <b>accounting offset</b>, not cash — the customer no longer owes that portion once a CN is applied.
                By the time we compute "Promised", CN-cleared amounts have <b>already left the balance</b>. So CN does <b>not</b> add to Promised.
              </p>
              <p className="text-[11px] text-slate-600 mt-1.5">
                If you want the "grossed-up" original view: <span className="font-mono bg-slate-100 rounded px-1.5 py-0.5">Original billing ≈ Promised + CN Applied</span>.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-rose-500" /> Slipped Promises is a <b>subset</b>
              </h3>
              <p>
                Counts invoices whose <b>current expected date &gt; original expected date</b> — the promise was pushed later.
                It <b>overlaps</b> with Payments and Due (an invoice can slip, then partially pay). Do <b>not</b> add it to the strip totals.
              </p>
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" /> Why totals differ from <b>Billing vs Collections</b>
              </h3>
              <p>Two independent reasons:</p>
              <ol className="list-decimal ml-5 space-y-1.5 mt-1">
                <li>
                  <b>Slip Tracker only tracks invoices with an <span className="font-mono text-[11px] bg-slate-100 rounded px-1 py-0.5">Expected Payment Date</span> set in Zoho.</b> If that custom field is blank, the invoice is invisible here.
                  Fix: ask the salesperson to fill it, or run <span className="font-mono text-[11px] bg-slate-100 rounded px-1 py-0.5">Backfill originals</span>.
                </li>
                <li>
                  <b>Promised is post-CN; Billing is gross.</b> For the tracked invoices, gross ≈ <span className="font-mono">Promised + CN Applied</span>. The remaining gap = billing of the invoices that are missing an expected date.
                </li>
              </ol>
              {totP > 0 && (
                <div className="bg-slate-50 border border-slate-200 rounded-md px-3 py-2 mt-2 text-[11.5px] font-mono tabular-nums text-slate-800">
                  <div className="flex justify-between"><span>Promised (this window)</span><span>{fmtInr(totP)}</span></div>
                  <div className="flex justify-between"><span>+ Credit Notes Applied</span><span className="text-violet-700">{fmtInr(totCN)}</span></div>
                  <div className="flex justify-between border-t border-slate-300 mt-1 pt-1 font-bold"><span>≈ Gross billing of tracked inv</span><span>{fmtInr(totP + totCN)}</span></div>
                </div>
              )}
            </section>

            <section>
              <h3 className="font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-sky-500" /> Field-by-field
              </h3>
              <ul className="space-y-1.5">
                <li><b>Promised this window</b> — sum of <span className="font-mono text-[11px] bg-slate-100 rounded px-1">balance + received-in-window</span> for every invoice whose expected date falls in the range.</li>
                <li><b>Actual Payments</b> — cash actually received (payment.date inside the range), applied per invoice using Zoho&apos;s <span className="font-mono text-[11px] bg-slate-100 rounded px-1">amount_applied</span>.</li>
                <li><b>Credit Notes Applied</b> — CN posted against these invoices on/before window end. Informational only — never part of "Promised".</li>
                <li><b>Expected Collection (Due)</b> — outstanding balance still owed = <span className="font-mono text-[11px] bg-slate-100 rounded px-1">bcy_balance</span> from Zoho.</li>
                <li><b>Slipped Promises</b> — subset of tracked invoices where current expected &gt; original expected.</li>
              </ul>
            </section>
          </div>

          <div className="pt-3 border-t border-slate-100 flex justify-end">
            <button type="button" onClick={() => setExplainerOpen(false)}
              className="text-[11px] px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-700"
              data-testid="slip-explainer-close">Got it</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Invoice deep-detail modal ─────────────────────── */}
      <SlipInvoiceDetailModal
        open={!!detailInvoiceId}
        onOpenChange={(o) => { if (!o) setDetailInvoiceId(null); }}
        invoiceId={detailInvoiceId}
        zohoOrgId={zohoOrgId}
      />
    </Dialog>
  );
};

export default CollectionPlanTrackerModal;

// ─── Compact popover: Month / Quarter / FY ─────────────────────────
const MoreRanges = ({ monthPick, quarterPick, fyPick, setMonthPick, setQuarterPick, setFyPick }) => {
  const [open, setOpen] = React.useState(false);
  const active = monthPick || quarterPick || fyPick;
  const label = quarterPick ? `FY${String(Number(quarterPick.split('-')[0]) + 1).slice(-2)} ${quarterPick.split('-')[1]}`
              : fyPick ? `FY${String(Number(fyPick) + 1).slice(-2)}`
              : monthPick ? monthPick
              : 'More…';
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="slip-more-ranges"
        className={`px-2 py-1 rounded-md border transition ${active ? 'bg-rose-600 text-white border-rose-600' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
        {label} <ChevronDown className="w-3 h-3 inline-block ml-0.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg p-2 min-w-[220px] space-y-2">
            <label className="flex items-center gap-1 text-[10px] text-slate-500">
              <span className="w-14 text-slate-400">Month</span>
              <input type="month" value={monthPick} onChange={(e) => setMonthPick(e.target.value)} data-testid="slip-month-pick"
                className={`flex-1 text-[11px] border rounded-md px-1.5 py-0.5 ${monthPick ? 'border-rose-500 bg-rose-50' : 'border-slate-200'}`} />
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500">
              <span className="w-14 text-slate-400">Quarter</span>
              <select value={quarterPick} onChange={(e) => setQuarterPick(e.target.value)} data-testid="slip-quarter-pick"
                className={`flex-1 text-[11px] border rounded-md px-1.5 py-0.5 ${quarterPick ? 'border-rose-500 bg-rose-50' : 'border-slate-200'}`}>
                <option value="">—</option>
                {[currentFy(), currentFy() - 1, currentFy() - 2].flatMap((fy) =>
                  [1, 2, 3, 4].map((q) => {
                    const l = `FY${String(fy + 1).slice(-2)} Q${q}`;
                    return <option key={`${fy}-Q${q}`} value={`${fy}-Q${q}`}>{l}</option>;
                  })
                )}
              </select>
            </label>
            <label className="flex items-center gap-1 text-[10px] text-slate-500">
              <span className="w-14 text-slate-400">FY</span>
              <select value={fyPick} onChange={(e) => setFyPick(e.target.value)} data-testid="slip-fy-pick"
                className={`flex-1 text-[11px] border rounded-md px-1.5 py-0.5 ${fyPick ? 'border-rose-500 bg-rose-50' : 'border-slate-200'}`}>
                <option value="">—</option>
                {[currentFy(), currentFy() - 1, currentFy() - 2, currentFy() - 3].map((fy) => (
                  <option key={fy} value={fy}>FY{String(fy + 1).slice(-2)} · {fy}-{fy + 1}</option>
                ))}
              </select>
            </label>
          </div>
        </>
      )}
    </div>
  );
};

// ─── Consolidated tools menu: CSV, PDF, Backfill ───────────────────
const ToolsMenu = ({ onExportCsv, onExportPdf, onBackfill, backfilling, isAdmin }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} data-testid="slip-tools-menu"
        title="Export & tools"
        className="px-2 py-1 rounded-md border border-slate-200 text-slate-600 bg-white hover:bg-slate-50 flex items-center gap-1">
        Tools <ChevronDown className="w-3 h-3" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-50 mt-1 bg-white border border-slate-200 rounded-md shadow-lg py-1 min-w-[170px]">
            <button type="button" onClick={() => { setOpen(false); onExportCsv(); }} data-testid="slip-export-csv"
              className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-emerald-700">
              <Download className="w-3 h-3" /> Export CSV
            </button>
            <button type="button" onClick={() => { setOpen(false); onExportPdf(); }} data-testid="slip-export-pdf"
              className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-rose-700">
              <FileText className="w-3 h-3" /> Export PDF
            </button>
            <div className="border-t border-slate-100 my-1" />
            {isAdmin && (
              <button type="button" onClick={() => { setOpen(false); onBackfill(); }} disabled={backfilling}
                data-testid="slip-backfill"
                className="w-full text-left text-[11px] px-3 py-1.5 hover:bg-slate-50 flex items-center gap-2 text-slate-700 disabled:opacity-60">
                <RefreshCw className={`w-3 h-3 ${backfilling ? 'animate-spin' : ''}`} /> {backfilling ? 'Backfilling…' : 'Backfill originals'}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
